import { DocumentOntologyCatalog } from '@/services/database/documentIntelligence';

import {
    DOCUMENT_WORD_PATTERN,
    DocumentConceptTextReference,
    matchDocumentConcepts,
} from '../AttachmentRedactionEditor/documentConceptMatcher';
import {
    DocumentConcept,
    DocumentConceptReference,
    DocumentIntelligence,
    EMPTY_DOCUMENT_INTELLIGENCE,
} from '../AttachmentRedactionEditor/documentIntelligence';
import { buildDocxExtractedDocument, ExtractedDocument } from '../documentExtraction';

interface TextNodeRange {
    end: number;
    node: Text;
    start: number;
}

export interface RenderedDocxPage {
    element: HTMLElement;
    page: number;
    ranges: TextNodeRange[];
    text: string;
}

export interface RenderedDocxIntelligence {
    document: ExtractedDocument;
    intelligence: DocumentIntelligence;
    pages: RenderedDocxPage[];
}

const clamp = (value: number) => Math.min(Math.max(value, 0), 1);

const getTextRanges = (element: HTMLElement) => {
    const ranges: TextNodeRange[] = [];
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    let text = '';
    let node = walker.nextNode();

    while (node) {
        if (node instanceof Text && node.data) {
            const start = text.length;

            text += node.data;
            ranges.push({ end: text.length, node, start });
        }
        node = walker.nextNode();
    }

    return { ranges, text };
};

const getBoundary = (ranges: TextNodeRange[], offset: number, endBoundary = false) => {
    const range =
        ranges.find((candidate) =>
            endBoundary
                ? candidate.start < offset && candidate.end >= offset
                : candidate.start <= offset && candidate.end > offset,
        ) ?? ranges.at(endBoundary ? -1 : 0);

    if (!range) {
        return undefined;
    }

    return { node: range.node, offset: Math.min(Math.max(offset - range.start, 0), range.node.length) };
};

const getReference = (
    page: RenderedDocxPage,
    match: DocumentConceptTextReference,
): DocumentConceptReference | undefined => {
    const start = getBoundary(page.ranges, match.start);
    const end = getBoundary(page.ranges, match.end, true);

    if (!start || !end) {
        return undefined;
    }

    const range = document.createRange();

    range.setStart(start.node, start.offset);
    range.setEnd(end.node, end.offset);
    const pageBounds = page.element.getBoundingClientRect();

    if (!pageBounds.width || !pageBounds.height) {
        return undefined;
    }

    const rectangles = Array.from(range.getClientRects())
        .map((rectangle) => {
            const x = clamp((rectangle.left - pageBounds.left) / pageBounds.width);
            const y = clamp((rectangle.top - pageBounds.top) / pageBounds.height);
            const right = clamp((rectangle.right - pageBounds.left) / pageBounds.width);
            const bottom = clamp((rectangle.bottom - pageBounds.top) / pageBounds.height);

            return { height: bottom - y, width: right - x, x, y };
        })
        .filter((rectangle) => rectangle.width > 0 && rectangle.height > 0);

    if (!rectangles.length) {
        return undefined;
    }

    const x = Math.min(...rectangles.map((rectangle) => rectangle.x));
    const y = Math.min(...rectangles.map((rectangle) => rectangle.y));
    const right = Math.max(...rectangles.map((rectangle) => rectangle.x + rectangle.width));
    const bottom = Math.max(...rectangles.map((rectangle) => rectangle.y + rectangle.height));

    return {
        context: match.context,
        height: bottom - y,
        id: match.id,
        page: page.page,
        rectangles,
        textEnd: match.end,
        textStart: match.start,
        text: match.text,
        width: right - x,
        x,
        y,
    };
};

export const buildRenderedDocxIntelligence = (
    container: HTMLElement,
    ontology: DocumentOntologyCatalog,
    input: { mimeType: string; name: string } = {
        mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        name: 'Document',
    },
): RenderedDocxIntelligence => {
    const renderedSections = Array.from(container.querySelectorAll<HTMLElement>('section.attachment-docx'));
    const pageElements = renderedSections.length ? renderedSections : [container];
    const pages = pageElements.map((element, index): RenderedDocxPage => {
        const { ranges, text } = getTextRanges(element);

        element.style.position = 'relative';
        return { element, page: index + 1, ranges, text };
    });
    const concepts = matchDocumentConcepts(pages, ontology).flatMap<DocumentConcept>((concept) => {
        const references = concept.references.flatMap<DocumentConceptReference>((match) => {
            const page = pages.find((candidate) => candidate.page === match.page);
            const reference = page ? getReference(page, match) : undefined;

            return reference ? [reference] : [];
        });

        return references.length ? [{ ...concept, references }] : [];
    });
    const wordCount = pages.reduce((total, page) => total + [...page.text.matchAll(DOCUMENT_WORD_PATTERN)].length, 0);

    return {
        document: buildDocxExtractedDocument(container, input),
        intelligence:
            concepts.length || wordCount
                ? {
                      concepts,
                      domains: ontology.domains,
                      ontologyVersion: ontology.version,
                      provenance: { extractionMethod: 'docx-rendered-text', processingBoundary: 'client' },
                      schemes: ontology.schemes,
                      wordCount,
                  }
                : EMPTY_DOCUMENT_INTELLIGENCE,
        pages,
    };
};
